// pages/Homepage/Homepage.tsx
import React, { useEffect } from "react";
import {
  Box,
  useTheme,
  useMediaQuery,
  styled,
} from "@mui/material";
import AOS from "aos";
import "aos/dist/aos.css";

// Components
import HeroSection from "./HeroSection";
import WhoPerformsSection from "./WhoPerforms";
import WhoHostsSection from "./WhoHosts";
import WhoManagesSection from "./WhoManages";
import WhoPromotesSection from "./WhoPromote";
import NextGeneration from "./NextGeneration";
import WhyChooseTaaSNet from "./WhyTaasNet";
import Footer from "../../components/Footer/Footer";
import Navbar from "./LandingPageNabar";
import LandingHeader from "./LandingHeader";

/* ——— Netflix-ish tokens ——— */
const COLORS = {
  bg: "#0b0b0b",
  panel: "#141414",
  border: "rgba(255,255,255,0.12)",
  text: "#ffffff",
  textMuted: "rgba(255,255,255,0.75)",
  red: "#E50914",
};

/* Optional CTA styles you already had (kept for future use) */
const GradientButton = styled("button")(() => ({
  fontFamily: '"Lexend", sans-serif',
  background: "linear-gradient(90deg, #6366F1 0%, #9F03FF 100%)",
  color: "#fff",
  fontWeight: 700,
  border: "none",
  borderRadius: "8px",
  padding: "12px 24px",
  fontSize: "1rem",
  marginRight: "12px",
  cursor: "pointer",
  transition: "all 0.3s ease",
  "&:hover": {
    transform: "translateY(-2px)",
    boxShadow: "0 6px 14px rgba(99,102,241,0.4)",
  },
}));

const OutlinedButton = styled("button")(() => ({
  fontFamily: '"Lexend", sans-serif',
  backgroundColor: "transparent",
  color: "#6366F1",
  border: "2px solid #6366F1",
  fontWeight: 600,
  borderRadius: "8px",
  padding: "12px 24px",
  fontSize: "1rem",
  cursor: "pointer",
  transition: "all 0.3s ease",
  "&:hover": {
    backgroundColor: "#f2f2f2",
    transform: "translateY(-2px)",
  },
}));

/* Layout wrappers */
const Page = styled(Box)({
  minHeight: "100vh",
  backgroundColor: COLORS.bg,
  color: COLORS.text,
  // subtle vignette to feel like Netflix
  position: "relative",
  overflowX: "hidden",
});

const Content = styled(Box)(({ theme }) => ({
  maxWidth: 1440,
  margin: "0 auto",
  paddingInline: 16,
  [theme.breakpoints.up("sm")]: { paddingInline: 24 },
  [theme.breakpoints.up("md")]: { paddingInline: 40 },
}));

const SectionBreak = styled(Box)({
  borderTop: `1px solid ${COLORS.border}`,
  marginTop: 48,
  paddingTop: 48,
});

export default function Homepage() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  useEffect(() => {
    AOS.init({ duration: 800, once: true });
  }, []);

  // toolbar height for sticky AppBar offset on anchor scroll
  const headerHeight = isMobile ? 56 : 64;

  return (
    <Page>
      {/* Dark sticky header (uses your LandingHeader component) */}
      <LandingHeader />

      {/* If you re-enable your Navbar, keep it just under the header */}
      {/* <Navbar /> */}

      {/* Main content */}
      <Content sx={{ pt: 3, pb: { xs: 6, md: 8 } }}>
        {/* HERO — already Netflix-styled in your HeroSection */}
        <HeroSection />

        <SectionBreak>
          <WhyChooseTaaSNet />
        </SectionBreak>

        <SectionBreak id="who-performs" sx={{ scrollMarginTop: `${headerHeight + 16}px` }}>
          <WhoPerformsSection />
        </SectionBreak>

        <SectionBreak id="who-manages" sx={{ scrollMarginTop: `${headerHeight + 16}px` }}>
          <WhoManagesSection />
        </SectionBreak>

        <SectionBreak id="who-promotes" sx={{ scrollMarginTop: `${headerHeight + 16}px` }}>
          <WhoPromotesSection />
        </SectionBreak>

        <SectionBreak id="who-hosts" sx={{ scrollMarginTop: `${headerHeight + 16}px` }}>
          <WhoHostsSection />
        </SectionBreak>

        <SectionBreak
          id="next-generation"
          sx={{
            minHeight: "70vh",
            scrollMarginTop: `${headerHeight + 16}px`,
          }}
        >
          <NextGeneration />
        </SectionBreak>

        <Box sx={{ mt: 6 }}>
          <Footer />
        </Box>
      </Content>
    </Page>
  );
}
